/**
 * OCR 缓存策略
 * 提供条件缓存、TTL 管理和缓存大小控制
 */

import {
  generateCacheKey,
  storeCache,
  getCache,
  clearExpired,
  trimCache,
  getCacheCount,
  DEFAULT_CONFIG,
} from './indexedDbCache.js';

// OCR 缓存配置（DEFAULT_CONFIG 的副本，可独立修改）
const OCR_CACHE_CONFIG = { ...DEFAULT_CONFIG };

/**
 * 检查响应是否为错误响应（不应被缓存）
 * 覆盖 http.js 中所有可能的错误场景
 * @param {Object} response - OCR 接口响应
 * @returns {boolean} 如果是错误响应返回 true
 */
export function isErrorResponse(response) {
  // 1. 空响应或 undefined（请求被取消等场景）
  if (!response || response === undefined) {
    return true;
  }
  
  // 2. error 字段为 true（最常见的错误标识）
  // 覆盖场景：敏感图片、业务码非0、网络错误、云API异常等
  if (response.error === true) {
    return true;
  }
  
  // 3. 存在 Error 对象（云 API 异常）
  // http.js line 74: if (Error) { ... }
  if (response.Error) {
    return true;
  }
  
  // 4. JobStatus 为 FAIL（轮询任务失败）
  if (response.JobStatus === 'FAIL') {
    return true;
  }
  
  // 5. isImageError 为 true（图片相关错误，如敏感图片）
  // http.js line 37: { error: true, isImageError: true, ... }
  if (response.isImageError === true) {
    return true;
  }
  
  // 6. 存在 code 字段且非成功码（业务错误）
  // http.js line 56: { code: response.data.code, error: true, ... }
  // 注意：code 为 0 表示成功，非 0 表示失败
  if (response.code !== undefined && response.code !== 0 && response.code !== '0') {
    return true;
  }
  
  return false;
}

/**
 * 检查请求是否应该被缓存
 * @param {Object} requestConfig - baseOcrRequest 的配置对象
 * @param {Object} config - 缓存配置
 * @returns {boolean} 是否应该缓存
 */
export function shouldCache(requestConfig, config = {}) {
  const mergedConfig = { ...OCR_CACHE_CONFIG, ...config };
  
  // 检查是否启用缓存
  if (!mergedConfig.enableCache) {
    return false;
  }
  
  // 检查是否在跳过缓存的命令列表中
  if (mergedConfig.skipCacheCmds.includes(requestConfig.cmd)) {
    return false;
  }
  
  // 只有 isExample 为 true 的请求才缓存
  if (!requestConfig.isExample) {
    return false;
  }
  
  return true;
}

/**
 * 获取缓存的响应
 * @param {Object} requestConfig - baseOcrRequest 的配置对象
 * @param {Object} config - 缓存配置
 * @returns {Promise<Object|null>} 缓存的响应或 null
 */
export async function getCachedResponse(requestConfig, config = {}) {
  const mergedConfig = { ...OCR_CACHE_CONFIG, ...config };
  
  if (!shouldCache(requestConfig, mergedConfig)) {
    return null;
  }
  
  const key = generateCacheKey(requestConfig);
  const cachedEntry = await getCache(key, mergedConfig);
  
  if (!cachedEntry) {
    return null;
  }
  
  return cachedEntry.response;
}

/**
 * 存储请求响应到缓存
 * @param {Object} requestConfig - baseOcrRequest 的配置对象
 * @param {Object} response - 响应数据
 * @param {Object} config - 缓存配置
 * @param {number} [timestamp] - 可选的时间戳（毫秒），用于记录最后获取结果的时间，默认为当前时间
 * @returns {Promise<boolean>} 是否成功存储（错误响应不会存储）
 */
export async function storeCachedResponse(requestConfig, response, config = {}, timestamp = null) {
  const mergedConfig = { ...OCR_CACHE_CONFIG, ...config };
  
  if (!shouldCache(requestConfig, mergedConfig)) {
    return false;
  }
  
  // 检查是否为错误响应，错误响应不缓存
  if (isErrorResponse(response)) {
    console.log(`[OCR Cache] 跳过缓存错误响应: ${requestConfig.cmd}`);
    return false;
  }
  
  const key = generateCacheKey(requestConfig);
  await storeCache(key, requestConfig, response, mergedConfig, timestamp);
  
  // 存储后检查并修剪缓存大小
  await trimCache(mergedConfig);
  return true;
}

/**
 * 带缓存的 OCR 请求
 * @param {Function} originalRequest - 原始的 OCR 请求函数（如 baseOcrRequest）
 * @param {Object} requestConfig - baseOcrRequest 的配置对象
 * @param {Object} cacheConfig - 缓存配置
 * @returns {Promise<Object>} 响应数据
 */
export async function cachedOcrRequest(originalRequest, requestConfig, cacheConfig = {}) {
  const mergedConfig = { ...OCR_CACHE_CONFIG, ...cacheConfig };
  
  // 检查是否应该尝试从缓存读取
  if (shouldCache(requestConfig, mergedConfig)) {
    const cachedResponse = await getCachedResponse(requestConfig, mergedConfig);
    if (cachedResponse) {
      console.log(`[OCR Cache] 缓存命中: ${requestConfig.cmd}`);
      return cachedResponse;
    }
  }
  
  // 没有缓存，发起实际请求
  console.log(`[OCR Cache] 缓存未命中，发起请求: ${requestConfig.cmd}`);
  const response = await originalRequest(requestConfig);
  
  // 存储响应到缓存（如果是示例图片且不是错误响应）
  if (shouldCache(requestConfig, mergedConfig)) {
    // isErrorResponse 检查已在 storeCachedResponse 内部处理
    try {
      const stored = await storeCachedResponse(requestConfig, response, mergedConfig);
      if (stored) {
        console.log(`[OCR Cache] 缓存已存储: ${requestConfig.cmd}`);
      }
    } catch (error) {
      console.error('[OCR Cache] 存储缓存失败:', error);
    }
  }
  
  return response;
}

/**
 * 清空所有缓存
 * @param {Object} config - 缓存配置
 * @returns {Promise<void>}
 */
export async function clearCache(config = {}) {
  const mergedConfig = { ...OCR_CACHE_CONFIG, ...config };
  const { clearAllCache } = await import('./indexedDbCache.js');
  await clearAllCache(mergedConfig);
}

/**
 * 清理过期缓存
 * @param {Object} config - 缓存配置
 * @returns {Promise<number>} 清理的条目数量
 */
export async function cleanupExpiredCache(config = {}) {
  const mergedConfig = { ...OCR_CACHE_CONFIG, ...config };
  return await clearExpired(mergedConfig);
}

/**
 * 获取缓存统计信息
 * @param {Object} config - 缓存配置
 * @returns {Promise<Object>} 统计信息
 */
export async function getCacheStats(config = {}) {
  const mergedConfig = { ...OCR_CACHE_CONFIG, ...config };
  const count = await getCacheCount(mergedConfig);
  
  return {
    totalItems: count,
    maxItems: mergedConfig.maxItems,
    ttl: mergedConfig.ttl,
    skipCacheCmds: mergedConfig.skipCacheCmds,
    enableCache: mergedConfig.enableCache,
  };
}

/**
 * 更新缓存配置
 * @param {Object} newConfig - 新的配置
 * @returns {Object} 更新后的完整配置
 */
export function updateCacheConfig(newConfig) {
  Object.assign(OCR_CACHE_CONFIG, newConfig);
  return { ...OCR_CACHE_CONFIG };
}

/**
 * 获取当前缓存配置
 * @returns {Object} 当前配置
 */
export function getCacheConfig() {
  return { ...OCR_CACHE_CONFIG };
}

export default {
  isErrorResponse,
  shouldCache,
  getCachedResponse,
  storeCachedResponse,
  cachedOcrRequest,
  clearCache,
  cleanupExpiredCache,
  getCacheStats,
  updateCacheConfig,
  getCacheConfig,
};